// fastShots.ts

import * as React from "react";
import {
  Box,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PbvRally, PbvShot } from "./RallyBoardPopup";

import Replay10Icon from "@mui/icons-material/Replay10";
import Forward10Icon from "@mui/icons-material/Forward10";
import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import type {
  KitchenArrivalRow,
  ShotNumberOverlay,
  Vec3,
} from "./RallyBoardPopup";
import { ShotNumberOverlayLayer } from "./RallyPopup";
import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import { FirefightClip } from "./GameStory_firefights";
import {
  iconFastShot,
  iconLob,
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import { PBVShot } from "./pbvTypes";
import { ActiveRallyVideo } from "./ActiveRallyVideo";

export function VideoOverlayControls(props: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  rate: 0.25 | 0.5 | 1;
  setRate: (r: 0.25 | 0.5 | 1) => void;
  paused: boolean;
  setPaused: (p: boolean) => void;
}) {
  const { videoRef, rate, setRate, paused, setPaused } = props;

  const applyRate = React.useCallback(
    (r: 0.25 | 0.5 | 1) => {
      const v = videoRef.current;
      if (!v) return;
      v.defaultPlaybackRate = r;
      v.playbackRate = r;
    },
    [videoRef],
  );

  // keep rate applied even if browser resets it
  React.useEffect(() => {
    applyRate(rate);
  }, [rate, applyRate]);

  const togglePlay = React.useCallback(() => {
    const v = videoRef.current;
    if (!v) return;

    if (v.paused) {
      v.play().catch(() => {});
      setPaused(false);
    } else {
      v.pause();
      setPaused(true);
    }
  }, [videoRef, setPaused]);

  const step = React.useCallback(
    (deltaSeconds: number) => {
      const v = videoRef.current;
      if (!v) return;

      // pause while stepping for precision
      v.pause();
      setPaused(true);

      seekBySeconds(v, deltaSeconds);
    },
    [videoRef, setPaused],
  );

  const RateButton = (r: 0.25 | 0.5 | 1) => (
    <Button
      key={r}
      size="small"
      variant={rate === r ? "contained" : "outlined"}
      onClick={() => {
        setRate(r);
        applyRate(r);
      }}
      sx={{
        textTransform: "none",
        minHeight: 22,
        py: 0,
        px: 1,
        fontWeight: 900,
        fontSize: 12,
        borderRadius: 999,
        boxShadow: rate === r ? "0 0 0 1px rgba(255,255,255,0.35)" : "none",
        bgcolor: rate === r ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.55)",
        color: rate === r ? "rgba(0,0,0,0.92)" : "rgba(255,255,255,0.92)",
        borderColor: rate === r ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.28)",
        "&:hover": {
          bgcolor: rate === r ? "rgba(255,255,255,0.98)" : "rgba(0,0,0,0.70)",
        },
      }}
    >
      {r}×
    </Button>
  );

  return (
    <Box
      sx={{
        position: "absolute",
        right: 10,
        bottom: 10,
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        pointerEvents: "auto",
        zIndex: 120,
      }}
    >
      {/* transport */}
      <Button
        size="small"
        onClick={() => step(-1)}
        sx={{
          minHeight: 22,
          py: 0,
          px: 1,
          fontWeight: 900,
          fontSize: 12,
          borderRadius: 999,
          bgcolor: "rgba(0,0,0,0.55)",
          color: "rgba(255,255,255,0.92)",
          border: "1px solid rgba(255,255,255,0.20)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.70)" },
        }}
        startIcon={<Replay10Icon sx={{ fontSize: 18 }} />}
      >
        -1s
      </Button>

      <Button
        size="small"
        onClick={togglePlay}
        sx={{
          minHeight: 22,
          py: 0,
          px: 1,
          fontWeight: 900,
          fontSize: 12,
          borderRadius: 999,
          bgcolor: paused ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.55)",
          color: paused ? "rgba(0,0,0,0.92)" : "rgba(255,255,255,0.92)",
          border: paused
            ? "1px solid rgba(0,0,0,0.18)"
            : "1px solid rgba(255,255,255,0.20)",
          "&:hover": {
            bgcolor: paused ? "rgba(255,255,255,0.98)" : "rgba(0,0,0,0.70)",
          },
        }}
        startIcon={
          paused ? (
            <PlayArrowIcon sx={{ fontSize: 18 }} />
          ) : (
            <PauseIcon sx={{ fontSize: 18 }} />
          )
        }
      >
        {paused ? "Play" : "Pause"}
      </Button>

      <Button
        size="small"
        onClick={() => step(1)}
        sx={{
          minHeight: 22,
          py: 0,
          px: 1,
          fontWeight: 900,
          fontSize: 12,
          borderRadius: 999,
          bgcolor: "rgba(0,0,0,0.55)",
          color: "rgba(255,255,255,0.92)",
          border: "1px solid rgba(255,255,255,0.20)",
          "&:hover": { bgcolor: "rgba(0,0,0,0.70)" },
        }}
        endIcon={<Forward10Icon sx={{ fontSize: 18 }} />}
      >
        +1s
      </Button>

      {/* rate */}
      <Box sx={{ display: "flex", gap: 0.6, ml: 0.5 }}>
        {RateButton(0.25)}
        {RateButton(0.5)}
        {RateButton(1)}
      </Box>
    </Box>
  );
}

/** ========= LOB LOGIC (the only real change vs fast shots) ========= */

export type LobEvent = {
  rallyIndex: number;
  lobShotIndex: number; // 0-based within overlays array
  lobShotNumber: number; // 1-based (overlay.shotNumber)
  team: 0 | 1;
  playerId: number;

  // segment window is “shot before lob” → “4 shots after lob”
  segmentStartShotIndex: number;
  segmentEndShotIndex: number;

  segmentStartMs: number;
  segmentEndMs: number;

  // for table: max height (ft-ish) from trajectory.peak.z (lob shot)
  maxHeight: number; // NaN if missing

  // optional UI bits
  isVolley?: boolean;
  strokeType?: string;
  shotType?: string;
};

export type LobClip = {
  key: string;
  event: LobEvent;
  clipStartMs: number;
  clipEndMs: number;
};

// function isLobOverlay(o: ShotNumberOverlay): boolean {
//   const s: PbvShot|null = o?.rawShot;
//   if (!s) return false;

//   if (s?.shot_type === "lob") return true;

//   if (s?.tags && typeof s.tags === "object" && s.tags["type;lob"] != null)
//     return true;

//   if (s?.resulting_ball_movement?.trajectory?.peak?.z != null && s.resulting_ball_movement.trajectory.peak.z > 10)
//     return true;

//   return false;
// }
function isLobOverlay(o: ShotNumberOverlay): boolean {
  const s: PbvShot | null = o?.rawShot;
  if (!s) return false;

  // Primary: PB Vision's explicit shot_type
  if (s.shot_type === "lob") return true;

  // Secondary: PB Vision tags (be tolerant of tag key variants that include "type;lob")
  const tags: any = (s as any)?.tags;
  if (tags && typeof tags === "object") {
    for (const k of Object.keys(tags)) {
      if (k === "type;lob" || k.includes("type;lob")) return true;
    }
  }

  return false;
}

function overlayStartMs(o: ShotNumberOverlay): number | null {
  const s: PbvShot | null = o?.rawShot;
  const t = s?.start_ms;
  return typeof t === "number" ? t : null;
}

function overlayEndMs(o: ShotNumberOverlay): number | null {
  const s: any = o?.rawShot;
  const t = s?.end_ms;
  return typeof t === "number" ? t : null;
}

function lobMaxHeight(o: ShotNumberOverlay): number {
  const s: any = o?.rawShot;
  const z = s?.resulting_ball_movement?.trajectory?.peak?.z;
  return typeof z === "number" ? z : Number.NaN;
}

export function computeLobEventsForRally(args: {
  rally: KitchenArrivalRow;
  rallyIndex: number;
  getTeamFromPlayerId: (playerId: number) => 0 | 1;

  // knobs
  preShots?: number; // default 1 (shot before)
  postShots?: number; // default 4 (3–4 after; we choose 4)
}): LobEvent[] {
  const preShots = args.preShots ?? 1;
  const postShots = args.postShots ?? 4;

  const shots = [
    ...(Array.isArray(args.rally?.shotNumberOverlays)
      ? args.rally.shotNumberOverlays
      : []),
  ].sort((a, b) => (a.rawShot?.start_ms ?? 0) - (b.rawShot?.start_ms ?? 0));

  const out: LobEvent[] = [];

  for (let i = 0; i < shots.length; i++) {
    const o = shots[i];
    if (!isLobOverlay(o)) continue;

    const s: any = o.rawShot;
    const playerId = Number(o.playerId ?? s?.player_id);
    if (!Number.isFinite(playerId)) continue;

    const startI = Math.max(0, i - preShots);
    const endI = Math.min(shots.length - 1, i + postShots);

    const startMs0 = overlayStartMs(shots[startI]);
    const endMs0 = overlayEndMs(shots[endI]);

    const rallyStartMs =
      typeof (args.rally as any)?.segmentStartMs === "number"
        ? (args.rally as any).segmentStartMs
        : null;
    const rallyEndMs =
      typeof (args.rally as any)?.segmentEndMs === "number"
        ? (args.rally as any).segmentEndMs
        : null;

    let segStartMs =
      startMs0 ??
      overlayStartMs(o) ??
      (typeof s?.start_ms === "number" ? s.start_ms : 0);
    let segEndMs =
      endMs0 ??
      overlayEndMs(o) ??
      (typeof s?.end_ms === "number" ? s.end_ms : segStartMs + 400);

    if (typeof rallyStartMs === "number")
      segStartMs = Math.max(segStartMs, rallyStartMs);
    if (typeof rallyEndMs === "number")
      segEndMs = Math.min(segEndMs, rallyEndMs);
    if (segEndMs - segStartMs < 400) segEndMs = segStartMs + 400;

    out.push({
      rallyIndex: args.rallyIndex,
      lobShotIndex: i,
      lobShotNumber: o.shotNumber,
      team: args.getTeamFromPlayerId(playerId),
      playerId,

      segmentStartShotIndex: startI,
      segmentEndShotIndex: endI,

      segmentStartMs: segStartMs,
      segmentEndMs: segEndMs,

      maxHeight: lobMaxHeight(o),

      isVolley: !!s?.is_volley,
      strokeType: s?.stroke_type,
      shotType: s?.shot_type,
    });
  }

  return out;
}

export function buildLobClip(event: LobEvent): LobClip {
  const clipStartMs = event.segmentStartMs;
  const clipEndMs = event.segmentEndMs;

  return {
    key: `r${event.rallyIndex}_lobS${event.lobShotIndex}_${Math.round(clipStartMs)}_${Math.round(clipEndMs)}`,
    event,
    clipStartMs,
    clipEndMs,
  };
}

export function buildRallySegmentUrl(args: {
  packageId: string;
  rallyIndex: number;
  startMs: number;
  endMs: number;
  download?: 0 | 1;
}): string {
  return (
    `/api/rally-segment-video` +
    `?packageId=${encodeURIComponent(args.packageId)}` +
    `&rallyIndex=${encodeURIComponent(String(args.rallyIndex))}` +
    `&startMs=${encodeURIComponent(String(args.startMs))}` +
    `&endMs=${encodeURIComponent(String(args.endMs))}` +
    `&download=${encodeURIComponent(String(args.download ?? 0))}`
  );
}

export function seekBySeconds(video: HTMLVideoElement, deltaSeconds: number) {
  const dur = Number.isFinite(video.duration) ? video.duration : null;
  const next = video.currentTime + deltaSeconds;
  if (dur == null) {
    video.currentTime = Math.max(0, next);
    return;
  }
  video.currentTime = Math.max(0, Math.min(dur, next));
}

function dist3(a: Vec3, b: Vec3) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const dz = b.z - a.z;
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

// If your trajectory locations are FEET:
function ftPerSecToMph(ftps: number) {
  return ftps * 0.681818; // 1 ft/s = 0.681818 mph
}

// If your trajectory locations are METERS, use this instead:
// function mPerSecToMph(mps: number) { return mps * 2.23694; }

// fastReactions.ts
export type FastReactionEvent = {
  rallyIndex: number;
  shotIndex: number;

  team: 0 | 1;
  playerId: number;

  reactionMs: number;
  thresholdMs: number;

  // shot timing for seeking / display
  shotStartMs: number;
  shotEndMs: number;

  // optional UI bits
  isVolley?: boolean;
  strokeType?: string;
  shotType?: string;
};

// function shotWentOut(shot: any): boolean {
//   // Don’t count if they go out.
//   // Your data has resulting_ball_movement.trajectory.end.zone and we’ve seen "out".
//   return shot?.resulting_ball_movement?.trajectory?.end?.zone === "out";
// }

// function shotWentOut(overlay: ShotNumberOverlay): boolean {
//   return (
//     overlay?.rawShot?.resulting_ball_movement?.trajectory?.end?.zone === "out"
//   );
// }

function shotIsBad(overlay: ShotNumberOverlay): boolean {
  const s = overlay?.rawShot;

  // OUT via trajectory end zone
  if (s?.resulting_ball_movement?.trajectory?.end?.zone === "out") return true;

  const faults = s?.errors?.faults;

  // If PBV flags net faults explicitly
  if (faults?.net === true) return true;
  //if (faults?.tape === true) return true;

  // Some datasets store as strings/arrays
  const faultStr = JSON.stringify(faults ?? {});
  if (faultStr.includes("net") || faultStr.includes("tape")) return true;

  return false;
}

export type FastReactionClip = {
  key: string;
  event: FastReactionEvent;

  // clip window you request from /api/rally-segment-video
  clipStartMs: number;
  clipEndMs: number;
};

export function buildFastReactionClip(args: {
  event: FastReactionEvent;
  // these are global knobs you can tweak later
  preMs?: number;
  postMs?: number;
  // optional clamp if you know rally bounds
  rallyStartMs?: number;
  rallyEndMs?: number;
}): FastReactionClip {
  const { event } = args;
  const preMs = args.preMs ?? 1200;
  const postMs = args.postMs ?? 900;

  let clipStartMs = event.shotStartMs - preMs;
  let clipEndMs = event.shotEndMs + postMs;

  if (typeof args.rallyStartMs === "number")
    clipStartMs = Math.max(clipStartMs, args.rallyStartMs);
  if (typeof args.rallyEndMs === "number")
    clipEndMs = Math.min(clipEndMs, args.rallyEndMs);

  // hard minimum size just to avoid tiny segments if something is weird
  if (clipEndMs - clipStartMs < 400) clipEndMs = clipStartMs + 400;

  return {
    key: `r${event.rallyIndex}_s${event.shotIndex}_t${event.thresholdMs}_${Math.round(clipStartMs)}_${Math.round(clipEndMs)}`,
    event,
    clipStartMs,
    clipEndMs,
  };
}

// FastReactionsPopup.tsx

type Mode = "thisRally" | "allRallies";

export function LobsPopup(props: {
  insightsToCvPlayerIndex: PlayerIndexMap;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;

  packageId: string;

  // current selection context
  activeRallyIndex: number | null;
  rows: KitchenArrivalRow[];

  // derived data that you compute outside (recommended)
  clipsThisRally: LobClip[];
  clipsAll: LobClip[];

  // optional helpers for display
  playerLabel?: (playerId: number) => string;
  renderPlayerIdentity: RenderPlayerIdentity;

  teamLabel?: (team: 0 | 1) => string;

  teamColor?: (team: 0 | 1, alpha?: number) => string;
  teamName?: (team: 0 | 1) => string; // e.g. "Us" / "Them" or "Team 0" / "Team 1"
  firefightsClips: FirefightClip[]; // <-- NEW, for overlay in video
  firefightRallySet: Set<number>; // <-- NEW, for conditional overlay in timeline
}) {
  const {
    open,
    anchorEl,
    onClose,
    packageId,
    activeRallyIndex,
    rows,
    clipsThisRally,
    clipsAll,
    playerLabel,
    teamLabel,
    firefightsClips,
    firefightRallySet,
    insightsToCvPlayerIndex,
    renderPlayerIdentity,
  } = props;

  //   const [mode, setMode] = React.useState<Mode>("thisRally");
  const clips = clipsAll; //mode === "thisRally" ? clipsThisRally : clipsAll;

  // keep a selected clip
  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    // When opening or switching modes, default select first clip
    if (!open) return;
    const first = clips[0]?.key ?? null;
    setSelectedKey(first);
  }, [open, activeRallyIndex]); // activeRallyIndex changes should reset selection

  const selected = clips.find((c) => c.key === selectedKey) ?? clips[0] ?? null;

  // Build URL for the selected clip
  const videoUrl = React.useMemo(() => {
    if (!selected) return null;
    return buildRallySegmentUrl({
      packageId,
      rallyIndex: selected.event.rallyIndex,
      startMs: Math.round(selected.clipStartMs),
      endMs: Math.round(selected.clipEndMs),
      download: 0, //1,
    });
  }, [packageId, selected?.key]);

  // const videoRef = React.useRef<HTMLVideoElement | null>(null);
  // const [playbackRate, setPlaybackRate] = React.useState<0.25 | 0.5 | 1>(1);

  const clipVideoRef = React.useRef<HTMLVideoElement | null>(null);
  const rallyVideoRef = React.useRef<HTMLVideoElement | null>(null);

  const [clipRate, setClipRate] = React.useState<0.25 | 0.5 | 1>(1);
  const [rallyRate, setRallyRate] = React.useState<0.25 | 0.5 | 1>(1);

  const clipShouldMute = clipRate < 1;
  const rallyShouldMute = rallyRate < 1;

  const [clipPaused, setClipPaused] = React.useState(false);
  const [rallyPaused, setRallyPaused] = React.useState(false);

  //   React.useEffect(() => {
  //     const v = videoRef.current;
  //     if (!v) return;

  //     // Force autoplay on src change
  //     const tryPlay = async () => {
  //       try {
  //         // ensure rate is applied before play
  //         v.playbackRate = playbackRate;
  //         await v.play();
  //       } catch {
  //         // autoplay can be blocked if not muted; user can click video once
  //       }
  //     };
  //     void tryPlay();
  //   }, [videoUrl]);

  //   const applyRateAndPlay = React.useCallback(
  //     (rateOverride?: 0.25 | 0.5 | 1) => {

  //       const v = videoRef.current;
  //       if (!v) return;

  //               console.log("rate now", v.playbackRate);

  //       const rate = rateOverride ?? desiredRateRef.current;
  //       v.playbackRate = rate;

  //       // ensure playing (don’t spam; one call is fine)
  //       v.play().catch(() => {});
  //               console.log("2 rate now", v.playbackRate);

  //     },
  //     [],
  //   );

  // const applyRate = React.useCallback((rate: 0.25 | 0.5 | 1) => {
  //   const v = videoRef.current;
  //   if (!v) return;

  //   // Chrome often respects defaultPlaybackRate more reliably across reload/loop.
  //   v.defaultPlaybackRate = rate;
  //   v.playbackRate = rate;

  //   console.log("applied rate", rate, "actual", v.playbackRate);
  // }, []);

  // React.useLayoutEffect(() => {
  //   if (!videoUrl) return;
  //   applyRate(playbackRate);
  // }, [videoUrl, playbackRate, applyRate]);

  //   React.useEffect(() => {
  //     // do it now...
  //     applyRateAndPlay();

  //     // ...and again on next frames (ref/video sometimes not ready yet)
  //     const id1 = requestAnimationFrame(() => applyRateAndPlay());
  //     // const id2 = requestAnimationFrame(() => applyRateAndPlay());

  //     return () => {
  //       cancelAnimationFrame(id1);
  //       //   cancelAnimationFrame(id2);
  //     };
  //   }, [videoUrl, applyRateAndPlay]);

  const [pos, setPos] = React.useState<{ x: number; y: number }>({
    x: 18,
    y: 90,
  });

  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      dragging: true,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;

      const nextX = Math.max(
        0,
        Math.min(window.innerWidth - 220, d.startPosX + dx),
      );
      const nextY = Math.max(
        0,
        Math.min(window.innerHeight - 140, d.startPosY + dy),
      );
      setPos({ x: nextX, y: nextY });
    };

    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pos.x, pos.y]);

  const desiredRateRef = React.useRef<0.25 | 0.5 | 1>(1);

  // React.useEffect(() => {
  //   desiredRateRef.current = playbackRate;
  //   // Apply instantly (no play spam)
  //   const v = videoRef.current;
  //   if (v) v.playbackRate = playbackRate;
  // }, [playbackRate]);
  //   type SortDir = "asc" | "desc";
  //   const [sortDir, setSortDir] = React.useState<SortDir>("asc");

  type SortKey = "height" | "rally";
  type SortDir = "asc" | "desc";

  const [sortKey, setSortKey] = React.useState<SortKey>("height");
  const [sortDir, setSortDir] = React.useState<SortDir>("desc");

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const [playerFilter, setPlayerFilter] = React.useState<number | null>(null);

  const filteredClips = React.useMemo(() => {
    if (playerFilter == null) return clips;
    return clips.filter((c) => c.event.playerId === playerFilter);
  }, [clips, playerFilter]);

  //const filteredClips = clips; // (we’ll insert player filtering below)
  const sortedClips = React.useMemo(() => {
    const arr = [...filteredClips];
    arr.sort((a, b) => {
      const av = sortKey === "height" ? a.event.maxHeight : a.event.rallyIndex;
      const bv = sortKey === "height" ? b.event.maxHeight : b.event.rallyIndex;
      const d = av - bv;
      return sortDir === "asc" ? d : -d;
    });
    return arr;
  }, [filteredClips, sortKey, sortDir]);

  const playerIds = React.useMemo(() => {
    const s = new Set<number>();
    for (const c of clips) s.add(c.event.playerId);
    return Array.from(s).sort((a, b) => a - b);
  }, [clips]);

  const selectedRow = React.useMemo(() => {
    if (!selected) return null;
    return props.rows?.[selected.event.rallyIndex] ?? null;
  }, [props.rows, selected?.key]);

  const [seedNonce, setSeedNonce] = React.useState(0);

  React.useEffect(() => {
    // bump nonce anytime the selected clip changes
    if (!selected) return;
    setSeedNonce((n) => n + 1);
  }, [
    selected?.event?.rallyIndex,
    selected?.event?.lobShotIndex,
    Math.round(selected?.clipStartMs ?? 0),
    Math.round(selected?.clipEndMs ?? 0),
  ]);

  const rallyVideoUrl = selectedRow?.videoUrl ?? null;

  const clipRow = React.useMemo(() => {
    if (!selected || !selectedRow || !videoUrl) return null;

    return {
      ...selectedRow,

      // drive the top viewer with the TRIMMED clip url
      videoUrl: videoUrl,

      // timeline base for overlays/looping/etc
      segmentStartMs: selected.clipStartMs,
      segmentEndMs: selected.clipEndMs,
    };
  }, [selectedRow, selected?.key, videoUrl]);

  // console.log("selectedRow", selectedRow);
  // console.log("rallyVideoUrl", rallyVideoUrl);

  //   React.useEffect(() => {
  //     if (!open) return;
  //     const first = sortedClips[0]?.key ?? null;
  //     setSelectedKey(first);
  //   }, [open, playerFilter, sortDir]); // keep it simple

  React.useEffect(() => {
    if (!open) return;
    const first = sortedClips[0]?.key ?? null;
    setSelectedKey(first);
  }, [open, playerFilter, sortKey, sortDir]);

  // const shouldMute = playbackRate < 1;

  // React.useEffect(() => {
  //   const v = videoRef.current;
  //   if (!v) return;

  //   v.muted = shouldMute;
  //   v.defaultMuted = shouldMute;

  //   // If it's playing, nudge it to keep going (Safari/Chrome can hiccup on mute toggles)
  //   if (!v.paused) v.play().catch(() => {});
  // }, [shouldMute, videoUrl]);

  React.useEffect(() => {
    const v = clipVideoRef.current;
    if (!v) return;
    v.muted = clipShouldMute;
    v.defaultMuted = clipShouldMute;
  }, [clipShouldMute, videoUrl]);

  React.useEffect(() => {
    const v = rallyVideoRef.current;
    if (!v) return;
    v.muted = rallyShouldMute;
    v.defaultMuted = rallyShouldMute;
  }, [rallyShouldMute, rallyVideoUrl]);

  React.useEffect(() => {
    setClipPaused(false);
  }, [videoUrl]);

  React.useEffect(() => {
    setRallyPaused(false);
  }, [rallyVideoUrl]);

  if (!open) return null;

  // NOTE: I'm not using MUI Popover here because you have your own floating panel patterns.
  // If you want this anchored like your other popups, swap outer container with Popover.
  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 1320,
        maxWidth: "calc(100vw - 36px)",
        height: "95vh",

        maxHeight: "calc(100vh - 120px)",
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 2000,
        border: "1px solid rgba(0,0,0,0.14)",

        // border: "3px solid yellow !important",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
        onMouseDown={onMouseDownHeader}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Typography sx={{ fontWeight: 900, mr: 1 }}>
            {iconLob(16)} Lobs
          </Typography>

          <Typography sx={{ opacity: 0.75, fontSize: 12 }}>
            {filteredClips.length} clips
          </Typography>

          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
            <Button
              size="small"
              variant={playerFilter == null ? "contained" : "outlined"}
              onClick={() => setPlayerFilter(null)}
              sx={{
                textTransform: "none",
                minHeight: 22,
                py: 0,
                fontWeight: 900,
                fontSize: 12,
              }}
            >
              All
            </Button>

            {playerIds.map((pid) => (
              <Button
                key={pid}
                size="small"
                variant={playerFilter === pid ? "contained" : "outlined"}
                onClick={() => setPlayerFilter(pid)}
                sx={{
                  textTransform: "none",
                  minHeight: 22,
                  py: 0,
                  fontWeight: 900,
                  fontSize: 12,
                }}
              >
                {playerLabel ? playerLabel(pid) : `P${pid}`}
              </Button>
            ))}
          </Box> */}

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setPlayerFilter(null)}
              sx={{
                textTransform: "none",
                minHeight: 22,
                py: 0,
                px: 1,
                minWidth: 0,
                fontWeight: 900,
                fontSize: 12,
                border: "1px solid transparent",
                bgcolor: playerFilter == null ? "rgba(0,0,0,0.07)" : "white",
                boxShadow:
                  playerFilter == null
                    ? "inset 0 0 0 1px rgba(0,0,0,0.20)"
                    : "none",
                "&:hover": {
                  borderColor: "rgba(0,0,0,0.18)",
                  bgcolor:
                    playerFilter == null
                      ? "rgba(0,0,0,0.10)"
                      : "rgba(0,0,0,0.03)",
                },
              }}
            >
              All
            </Button>

            {playerIds.map((pid) => {
              const isSelected = playerFilter === pid;
              const team = pid < 2 ? 0 : 1;

              return (
                <Button
                  key={pid}
                  size="small"
                  variant="text"
                  onClick={() => setPlayerFilter(pid)}
                  sx={{
                    textTransform: "none",
                    minHeight: 22,
                    py: 0,
                    px: 0.5,
                    minWidth: 0,
                    fontWeight: 900,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    border: "1px solid transparent",
                    bgcolor: isSelected
                      ? props.teamColor
                        ? props.teamColor(team, 0.18)
                        : "rgba(0,0,0,0.08)"
                      : "white",
                    boxShadow: isSelected
                      ? props.teamColor
                        ? `inset 0 0 0 1px ${props.teamColor(team, 0.55)}`
                        : "inset 0 0 0 1px rgba(0,0,0,0.22)"
                      : "none",
                    "&:hover": {
                      borderColor: props.teamColor
                        ? props.teamColor(team, 0.45)
                        : "rgba(0,0,0,0.18)",
                      bgcolor: isSelected
                        ? props.teamColor
                          ? props.teamColor(team, 0.24)
                          : "rgba(0,0,0,0.10)"
                        : "rgba(0,0,0,0.03)",
                    },
                  }}
                >
                  {renderPlayerIdentity
                    ? renderPlayerIdentity({
                        playerId: pid,
                        labelOnly: false,
                        size: 40,
                        borderRadius: 5,
                        fontSize: 12,
                      })
                    : playerLabel
                      ? playerLabel(pid)
                      : `p${pid}`}
                </Button>
              );
            })}
          </Box>
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ textTransform: "none", fontWeight: 900 }}
        >
          Close
        </Button>
      </Box>

      {/* Body */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Left: list/table */}
        <Box
          sx={{
            width: 420,
            maxWidth: "50%",
            borderRight: "1px solid rgba(0,0,0,0.12)",
            overflow: "auto",
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 900,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  onClick={() => toggleSort("rally")}
                >
                  Rally{" "}
                  {sortKey === "rally" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                </TableCell>
                <TableCell sx={{ fontWeight: 900 }}>Player</TableCell>
                <TableCell
                  sx={{
                    fontWeight: 900,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                  align="right"
                  //   onClick={() =>
                  //     setSortDir((d) => (d === "asc" ? "desc" : "asc"))
                  //   }
                  onClick={() => toggleSort("height")}
                >
                  Max Ht {sortDir === "asc" ? "▲" : "▼"}
                </TableCell>{" "}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedClips.map((c) => {
                const e = c.event;
                const isSel = c.key === selectedKey;
                return (
                  <TableRow
                    key={c.key}
                    hover
                    onClick={() => setSelectedKey(c.key)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: isSel ? "rgba(0,0,0,0.06)" : undefined,
                    }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {e.rallyIndex + 1}
                    </TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "center",
                          gap: 0.25,
                        }}
                      >
                        {/* <Box sx={{ fontWeight: 800, fontSize: 13 }}>
                          {playerLabel
                            ? playerLabel(e.playerId)
                            : `P${e.playerId}`}
                        </Box> */}

                        <Box sx={{ minWidth: 0 }}>
                          {renderPlayerIdentity ? (
                            renderPlayerIdentity({
                              playerId: e.playerId,
                              labelOnly: false,
                              size: 26,
                              borderRadius: 5,
                              fontSize: 12,
                            })
                          ) : (
                            <Box sx={{ fontWeight: 800, fontSize: 13 }}>
                              {playerLabel
                                ? playerLabel(e.playerId)
                                : `p${e.playerId}`}
                            </Box>
                          )}
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                            flexWrap: "wrap",
                          }}
                        >
                          <Box
                            sx={{
                              px: 0.8,
                              py: 0.15,
                              borderRadius: 999,
                              fontWeight: 900,
                              fontSize: 11,
                              lineHeight: 1.2,
                              bgcolor: props.teamColor
                                ? props.teamColor(e.team, 0.12)
                                : "rgba(0,0,0,0.08)",
                              color: props.teamColor
                                ? props.teamColor(e.team, 1)
                                : "inherit",
                              border: props.teamColor
                                ? `1px solid ${props.teamColor(e.team, 0.35)}`
                                : "1px solid rgba(0,0,0,0.10)",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {props.teamName
                              ? props.teamName(e.team)
                              : `Team ${e.team}`}
                          </Box>

                          <Box
                            sx={{
                              fontSize: 12,
                              opacity: 0.75,
                              whiteSpace: "nowrap",
                            }}
                          >
                            • shot {e.lobShotIndex + 1}
                            {e.isVolley ? " • volley" : ""}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900 }}>
                      {Math.round(e.maxHeight)} ft
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>

        {/* Right: video + overlay “enclosing stuff” */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ p: 1, borderBottom: "1px solid rgba(0,0,0,0.12)" }}>
            {selected ? (
              <Typography sx={{ fontWeight: 900 }}>
                Rally {selected.event.rallyIndex + 1} • Shot{" "}
                {selected.event.lobShotIndex + 1} •{" "}
                {Math.round(selected.event.maxHeight)} ft (approx)
              </Typography>
            ) : (
              <Typography sx={{ opacity: 0.75 }}>
                No lobs for this selection.
              </Typography>
            )}
          </Box>

          <Box>
            <CondensedRallyTimeline_ContextOnly
              activeRally={selectedRow}
              rows={rows}
              barsAreaHeightPx={40}
              showLegend={false}
              showRallyIndex={true}
              showWinnerAndServe={true}
              firefightRallySet={firefightRallySet}
            />
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              // display: "flex",
              flexDirection: "column",
              bgcolor: "black",
              display: "none",
            }}
          >
            {
              videoUrl && selected && videoUrl === "blah" ? (
                <Box
                  sx={{
                    position: "relative",
                    flex: 1,
                    minHeight: 0,
                    borderBottom: "1px solid rgba(255,255,255,0.10)",
                  }}
                >
                  <VideoOverlayControls
                    videoRef={clipVideoRef}
                    rate={clipRate}
                    setRate={setClipRate}
                    paused={clipPaused}
                    setPaused={setClipPaused}
                  />

                  <video
                    ref={clipVideoRef}
                    key={videoUrl}
                    src={videoUrl}
                    autoPlay
                    loop
                    playsInline
                    muted={clipShouldMute}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      const v = clipVideoRef.current;
                      if (!v) return;
                      if (v.paused) {
                        v.play().catch(() => {});
                        setClipPaused(false);
                      } else {
                        v.pause();
                        setClipPaused(true);
                      }
                    }}
                    onLoadedMetadata={() => {
                      const v = clipVideoRef.current;
                      if (!v) return;
                      v.defaultPlaybackRate = clipRate;
                      v.playbackRate = clipRate;
                    }}
                    onRateChange={() => {
                      const v = clipVideoRef.current;
                      if (!v) return;
                      if (Math.abs(v.playbackRate - clipRate) > 0.01) {
                        v.defaultPlaybackRate = clipRate;
                        v.playbackRate = clipRate;
                      }
                    }}
                  />

                  {/* ✅ ShotNumber (playerId) overlay */}
                  {selectedRow?.shotNumberOverlays && (
                    <ShotNumberOverlayLayer
                      focusedFilter={null}
                      videoRef={clipVideoRef}
                      overlays={selectedRow.shotNumberOverlays}
                      segmentStartMs={selected.clipStartMs} // <-- THIS is the key for clip video
                      highlightedShotNumber={selected.event.lobShotNumber}
                      highlightedExtraText={
                        selected.event.maxHeight?.toFixed(0) + "ft"
                      }
                      firefightsAll={firefightsClips}
                      rallyIndex={selectedRow.rallyIndex}
                    />
                  )}

                  {/* Overlay “enclosing stuff” */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: 10,
                      top: 10,
                      px: 1,
                      py: 0.6,
                      borderRadius: 1.5,
                      bgcolor: "rgba(0,0,0,0.65)",
                      color: "white",
                      fontSize: 12,
                      lineHeight: 1.1,
                      maxWidth: "calc(100% - 20px)",
                      pointerEvents: "none",

                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    {/* left: main label */}
                    <Box
                      sx={{
                        fontWeight: 900,
                        fontSize: 12.5,
                        whiteSpace: "nowrap",
                        flex: "0 0 auto",
                      }}
                    >
                      ⚡ Shot {selected.event.lobShotNumber}:{" "}
                      {Math.round(selected.event.maxHeight)}ft
                    </Box>

                    {/* separator */}
                    <Box sx={{ opacity: 0.55, flex: "0 0 auto" }}>•</Box>

                    {/* right: player/team (ellipsize if needed) */}
                    <Box
                      sx={{
                        opacity: 0.9,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                        flex: "1 1 auto",
                      }}
                      // title={`${playerLabel ? playerLabel(selected.event.playerId) : `Player ${selected.event.playerId}`} • ${
                      //   teamLabel
                      //     ? teamLabel(selected.event.team)
                      //     : `Team ${selected.event.team}`
                      // }`}

                      title={`${playerLabel ? playerLabel(selected.event.playerId) : `p${selected.event.playerId}`} • ${
                        teamLabel
                          ? teamLabel(selected.event.team)
                          : `Team ${selected.event.team}`
                      }`}
                    >
                      {playerLabel
                        ? playerLabel(selected.event.playerId)
                        : `p${selected.event.playerId}`}{" "}
                      •{" "}
                      {teamLabel
                        ? teamLabel(selected.event.team)
                        : `Team ${selected.event.team}`}
                    </Box>
                  </Box>
                </Box>
              ) : null
              // <Box sx={{ p: 2, color: "white", opacity: 0.75 }}>
              //   No clip selected.
              // </Box>
            }
          </Box>

          <Box
            sx={{
              position: "relative",
              flex: 1,
              minHeight: 0,
              borderBottom: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            {clipRow ? (
              <ActiveRallyVideo
                insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                aiContextLabel="Lobs"
                focusedFilter={null}
                packageId={packageId}
                row={clipRow}
                rows={rows}
                activeShotNumber={selected.event.lobShotNumber}
                showCondensedTimeline={false}
                fontColor={"white"}
                controlsPlacement={"left"}
                firefightsClips={firefightsClips}
                firefightRallySet={firefightRallySet}
                showRateControls={true}
                showTitle={false}
                showScoreEtc={false}
                showAi={true}
                aiDrawerMode={"split"}
                showLoopScrubber={true}
                defaultLoopUiOpen={false}
                showPopoutButton={false}
                isInFullPopup={false}
                seedPlayback={{
                  timeSec: 0,
                  paused: false,
                  nonce: seedNonce,
                }}
              />
            ) : null}

            {/* keep your existing top-left label overlay */}
            {/* <Box
              sx={{
                position: "absolute",
                left: 10,
                top: 10,
                px: 1,
                py: 0.6,
                borderRadius: 1.5,
                bgcolor: "rgba(0,0,0,0.65)",
                color: "white",
                fontSize: 12,
                lineHeight: 1.1,
                maxWidth: "calc(100% - 20px)",
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            > */}
            {/* Overlay “enclosing stuff” */}
            <Box
              sx={{
                position: "absolute",
                left: 10,
                top: 10,
                px: 1,
                py: 0.6,
                borderRadius: 1.5,
                bgcolor: "rgba(0,0,0,0.65)",
                color: "white",
                fontSize: 12,
                lineHeight: 1.1,
                maxWidth: "calc(100% - 20px)",
                pointerEvents: "none",

                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {/* left: main label */}
              <Box
                sx={{
                  fontWeight: 900,
                  fontSize: 12.5,
                  whiteSpace: "nowrap",
                  flex: "0 0 auto",
                }}
              >
                ⚡ Shot {selected.event.lobShotNumber}:{" "}
                {Math.round(selected.event.maxHeight)}ft
              </Box>

              {/* separator */}
              <Box sx={{ opacity: 0.55, flex: "0 0 auto" }}>•</Box>

              {/* right: player/team (ellipsize if needed) */}
              <Box
                sx={{
                  opacity: 0.9,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0,
                  flex: "1 1 auto",
                }}
                title={`${playerLabel ? playerLabel(selected.event.playerId) : `Player ${selected.event.playerId}`} • ${
                  teamLabel
                    ? teamLabel(selected.event.team)
                    : `Team ${selected.event.team}`
                }`}
              >
                {playerLabel
                  ? playerLabel(selected.event.playerId)
                  : `Player ${selected.event.playerId}`}{" "}
                •{" "}
                {teamLabel
                  ? teamLabel(selected.event.team)
                  : `Team ${selected.event.team}`}
              </Box>
            </Box>

            {/* leave the rest of your existing label contents as-is */}
            {/* (your ⚡ Shot ... ft + player/team line) */}
          </Box>
          {/* </Box> */}

          <Box sx={{ position: "relative", minHeight: 0, flex: 1 }}>
            {rallyVideoUrl && selectedRow && selected ? (
              <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
                <ActiveRallyVideo
                  insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                  aiContextLabel="Lobs"
                  focusedFilter={null}
                  packageId={packageId}
                  row={selectedRow}
                  rows={rows}
                  activeShotNumber={selected.event.lobShotNumber}
                  showCondensedTimeline={false}
                  fontColor={"white"}
                  controlsPlacement={"left"}
                  firefightsClips={firefightsClips}
                  firefightRallySet={firefightRallySet}
                  showRateControls={true}
                  showTitle={false}
                  showScoreEtc={false}
                  showAi={true}
                  aiDrawerMode={"split"}
                  showLoopScrubber={true}
                  defaultLoopUiOpen={false}
                  showPopoutButton={false}
                  isInFullPopup={false}
                  seedPlayback={{
                    timeSec: 0,
                    paused: false,
                    nonce: seedNonce,
                  }}
                />

                {/* keep your existing "Full rally" label overlay */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 10,
                    top: 10,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "white",
                    fontSize: 12,
                    pointerEvents: "none",
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: 900,
                      display: "inline-block",
                      marginRight: "4px",
                    }}
                  >
                    Full rally
                  </Box>
                  <Box sx={{ opacity: 0.85, display: "inline-block" }}>
                    Rally {selected.event.rallyIndex + 1}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 2, color: "white", opacity: 0.75 }}>
                No rally videoUrl on this row.
              </Box>
            )}

            {rallyVideoUrl && rallyVideoUrl === "blah" ? (
              <>
                <VideoOverlayControls
                  videoRef={rallyVideoRef}
                  rate={rallyRate}
                  setRate={setRallyRate}
                  paused={rallyPaused}
                  setPaused={setRallyPaused}
                />

                <video
                  ref={rallyVideoRef}
                  key={rallyVideoUrl}
                  src={rallyVideoUrl}
                  autoPlay
                  loop
                  playsInline
                  muted={rallyShouldMute}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    const v = rallyVideoRef.current;
                    if (!v) return;
                    if (v.paused) {
                      v.play().catch(() => {});
                      setRallyPaused(false);
                    } else {
                      v.pause();
                      setRallyPaused(true);
                    }
                  }}
                  onLoadedMetadata={() => {
                    const v = rallyVideoRef.current;
                    if (!v) return;
                    v.defaultPlaybackRate = rallyRate;
                    v.playbackRate = rallyRate;
                  }}
                  onRateChange={() => {
                    const v = rallyVideoRef.current;
                    if (!v) return;
                    if (Math.abs(v.playbackRate - rallyRate) > 0.01) {
                      v.defaultPlaybackRate = rallyRate;
                      v.playbackRate = rallyRate;
                    }
                  }}
                />
                {/* ✅ ShotNumber (playerId) overlay */}
                {selectedRow?.shotNumberOverlays && (
                  <ShotNumberOverlayLayer
                    focusedFilter={null}
                    videoRef={rallyVideoRef}
                    overlays={selectedRow.shotNumberOverlays}
                    segmentStartMs={selectedRow.segmentStartMs} // <-- THIS is the key for clip video
                    highlightedShotNumber={selected.event.lobShotNumber}
                    highlightedExtraText={
                      selected.event.maxHeight?.toFixed(0) + "ft"
                    }
                    firefightsAll={firefightsClips}
                    rallyIndex={selectedRow.rallyIndex}
                  />
                )}

                {/* <video
                  key={rallyVideoUrl}
                  src={rallyVideoUrl}
                  autoPlay
                  loop
                  playsInline
                  muted
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                  }}
                /> */}
                <Box
                  sx={{
                    position: "absolute",
                    left: 10,
                    top: 10,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: "rgba(0,0,0,0.55)",
                    color: "white",
                    fontSize: 12,
                    pointerEvents: "none",
                  }}
                >
                  <Box
                    sx={{
                      fontWeight: 900,
                      display: "inline-block",
                      marginRight: "4px",
                    }}
                  >
                    Full rally
                  </Box>
                  <Box sx={{ opacity: 0.85, display: "inline-block" }}>
                    Rally {selected?.event.rallyIndex + 1}
                  </Box>
                </Box>
              </>
            ) : (
              <Box sx={{ p: 2, color: "white", opacity: 0.75 }}>
                No rally videoUrl on this row.
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
